import type {
  BookingPolicyInput,
  BookingPolicyOutcome,
  PolicyInput,
  PolicyOutcome,
} from "./types.ts";

/**
 * The policy engine. Pure: no I/O, no clock, no database, no imports beyond types. The API enforces
 * with it and the mobile app previews with it, so a member never sees one answer and gets another.
 *
 * Invariants, all covered by tests in `tests/policy.test.ts`:
 *   1. A business actor always gets `penalty: "none"` and the credit back. Owners cannot configure
 *      this away; it is the trust promise of the product (PRD §6).
 *   2. A member acting before the cut-off gets `penalty: "none"` and the credit back.
 *   3. A member acting after the cut-off gets whatever `lateCancelRule` says.
 *   4. Reschedule limits produce `allowed: false` whatever the cut-off says, because the member has
 *      spent an allowance rather than missed a deadline.
 *   5. Enrollment bookings never move credits. A recurring slot is paid by a recurring fee, so
 *      cancelling one week frees the seat and costs nothing either way.
 *   6. A package's cancellation allowance is spent before the cut-off is read, and running out
 *      costs the session rather than refusing the cancellation. It never applies to a reschedule
 *      or to an enrollment booking. Null means unlimited, which is every business that has not set one.
 *   7. On a `late_only` package the allowance is read after the cut-off instead: an early cancel is
 *      always free and spends nothing, and a late cancel spends one only when that changes the
 *      result. Running out puts the member back on the late rule, never on something stricter.
 */

const MS_PER_HOUR = 3_600_000;

/**
 * Credits are indivisible integers in V1, so a "partial" late-cancel rule has to resolve to a whole
 * credit. We round half up, which puts ties in the member's favour: 5000 bps returns the credit,
 * 4999 bps does not. The exact configured share stays on the outcome as `partialBps` and is written
 * into the ledger note, so an owner disputing a case can see what the rule actually was.
 */
export function resolvePartialCredits(partialBps: number): number {
  return Math.round(partialBps / 10_000);
}

function hoursBeforeStart(sessionStartsAt: Date, now: Date): number {
  return (sessionStartsAt.getTime() - now.getTime()) / MS_PER_HOUR;
}

function creditsForFullReturn(bookingSource: PolicyInput["bookingSource"]): number {
  return bookingSource === "open" ? 1 : 0;
}

function actionNoun(action: PolicyInput["action"]): string {
  return action === "cancel" ? "cancellation" : "reschedule";
}

function actionVerb(action: PolicyInput["action"]): string {
  return action === "cancel" ? "cancel" : "reschedule";
}

function describeCutoff(hours: number): string {
  if (hours === 0) return "right up to the start time";
  if (hours === 1) return "up to 1 hour before the session";
  if (hours % 24 === 0 && hours >= 24) {
    const days = hours / 24;
    return days === 1 ? "up to 1 day before the session" : `up to ${days} days before the session`;
  }
  return `up to ${hours} hours before the session`;
}

export function evaluate(input: PolicyInput): PolicyOutcome {
  const { policy, sessionStartsAt, now, actor, bookingSource, action } = input;
  const fullReturn = creditsForFullReturn(bookingSource);

  // Invariant 1. The business cancelled or moved the session, so the member is made whole no matter
  // what the cut-off says and no matter how many times they have already rescheduled.
  if (actor === "business") {
    return {
      allowed: true,
      penalty: "none",
      creditDelta: fullReturn,
      reason: "business_cancel",
      explanation:
        fullReturn > 0
          ? "The business changed this session, so the session has gone back into your balance in full."
          : "The business changed this session. Your recurring slot is unaffected and nothing has been charged.",
    };
  }

  // Invariant 4. Allowances are checked before the clock, because running out of reschedules is a
  // different answer from missing the deadline and the member needs to hear the right one.
  if (action === "reschedule") {
    const perBooking = policy.rescheduleLimitPerBooking;
    if (perBooking !== null && input.rescheduleCount >= perBooking) {
      return {
        allowed: false,
        penalty: "none",
        creditDelta: 0,
        reason: "reschedule_limit_booking",
        explanation:
          perBooking === 0
            ? "This business does not allow bookings to be moved. You can cancel instead."
            : `You have already moved this booking ${input.rescheduleCount} ${
                input.rescheduleCount === 1 ? "time" : "times"
              }, and this business allows ${perBooking} per booking. You can cancel instead.`,
      };
    }

    const perMonth = policy.rescheduleLimitPerMonth;
    if (perMonth !== null && input.monthlyRescheduleCount >= perMonth) {
      return {
        allowed: false,
        penalty: "none",
        creditDelta: 0,
        reason: "reschedule_limit_month",
        explanation:
          perMonth === 0
            ? "This business does not allow bookings to be moved. You can cancel instead."
            : `You have used all ${perMonth} of this month's reschedules. You can cancel instead, or move this booking next month.`,
      };
    }

    // A session that has already started cannot be moved anywhere. Cancelling it is still a valid
    // action (it just runs into the late-cancel rule below), so this check is reschedule only.
    if (sessionStartsAt.getTime() <= now.getTime()) {
      return {
        allowed: false,
        penalty: "none",
        creditDelta: 0,
        reason: "session_started",
        explanation: "This session has already started, so it can no longer be moved.",
      };
    }
  }

  const hoursLeft = hoursBeforeStart(sessionStartsAt, now);
  const beforeCutoff = hoursLeft >= policy.cancelCutoffHours;

  /*
   * Invariant 6. The package's own cancellation allowance, checked before the clock for the same
   * reason the reschedule limits are: running out of cancellations is a different answer from
   * missing the deadline, and the member has to hear the right one.
   *
   * Cancelling is still allowed once the allowance is gone; it just stops being free. A hard refusal
   * would put the business on the phone for every cancellation after the third, which is the opposite of
   * what a self-serve product is for.
   *
   * Three deliberate limits. It applies to cancelling and not to rescheduling, because a reschedule
   * returns the credit and immediately spends it again on the new session, so charging for one would
   * quietly make moving a booking cost a session. It applies only where there is a credit to lose,
   * which excludes a fixed weekly slot. And `null` means unlimited, which is what every business had
   * before a package could carry a number.
   */
  const allowance = input.cancellationAllowance ?? null;
  const used = input.cancellationsUsed ?? 0;
  const countsAllowance = action === "cancel" && fullReturn > 0 && allowance !== null;
  /*
   * Invariant 7. A late_only package reads its allowance after the cut-off, below. Zero free
   * cancellations means "never free" whatever the mode says: on late_only it would mean the same as
   * no limit, which no owner means by typing a zero. The API saves zero as any_cancel; this keeps a
   * row written some other way from reading as the opposite.
   */
  const lateOnly = countsAllowance && input.freeCancellationMode === "late_only" && (allowance ?? 0) > 0;
  const allowanceSpent = countsAllowance && !lateOnly && used >= (allowance ?? 0);

  if (allowanceSpent) {
    return {
      allowed: true,
      penalty: "lose_credit",
      creditDelta: 0,
      reason: "cancellation_allowance_used",
      explanation:
        allowance === 0
          ? "This package does not include any free cancellations, so this session is used up if you confirm."
          : `You have used all ${allowance} of the free cancellations included with this package, so this session is used up if you confirm.`,
    };
  }

  // Invariant 2.
  if (beforeCutoff) {
    return {
      allowed: true,
      penalty: "none",
      creditDelta: fullReturn,
      reason: "before_cutoff",
      explanation:
        fullReturn > 0
          ? `Free ${actionNoun(action)}. You can ${actionVerb(action)} ${describeCutoff(
              policy.cancelCutoffHours,
            )}, so this session stays in your balance.`
          : `Free ${actionNoun(action)}. You can ${actionVerb(action)} ${describeCutoff(
              policy.cancelCutoffHours,
            )}, and your recurring slot next week is unaffected.`,
    };
  }

  // Invariant 3, and invariant 5 through `fullReturn` being 0 for enrollment bookings.
  const cutoffCopy = `The cut-off for this session was ${policy.cancelCutoffHours} ${
    policy.cancelCutoffHours === 1 ? "hour" : "hours"
  } before it starts, and that has passed.`;
  const late = afterCutoff(policy, fullReturn, cutoffCopy);

  /*
   * Invariant 7. A free late cancellation is spent only when it changes the result: a late rule
   * that already gives the session back, or a partial share that rounds up to a whole session,
   * leaves the allowance where it is.
   */
  if (lateOnly && allowance !== null && late.creditDelta < fullReturn) {
    const left = allowance - used;
    if (left > 0) {
      return {
        allowed: true,
        penalty: "none",
        creditDelta: fullReturn,
        reason: "late_cancel_waived",
        explanation: `${cutoffCopy} You have ${left} free late ${
          left === 1 ? "cancellation" : "cancellations"
        } left on this package. This cancellation uses 1, so this session stays in your balance.`,
      };
    }
    if (allowance > 0) {
      return {
        ...late,
        explanation: `${late.explanation} You have used all ${allowance} free late ${
          allowance === 1 ? "cancellation" : "cancellations"
        } on this package.`,
      };
    }
  }

  return late;
}

/** What the late rule alone says about a cancellation after the cut-off. */
function afterCutoff(
  policy: PolicyInput["policy"],
  fullReturn: number,
  cutoffCopy: string,
): PolicyOutcome {

  switch (policy.lateCancelRule) {
    case "return_credit":
      return {
        allowed: true,
        penalty: "none",
        creditDelta: fullReturn,
        reason: "after_cutoff",
        explanation:
          fullReturn > 0
            ? `${cutoffCopy} This business puts the session back anyway, so nothing is lost.`
            : `${cutoffCopy} Your recurring slot next week is unaffected and nothing has been charged.`,
      };

    case "partial": {
      const partialBps = policy.lateCancelPartialBps ?? 0;
      const returned = fullReturn === 0 ? 0 : resolvePartialCredits(partialBps);
      const percent = partialBps / 100;
      const percentCopy = Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(2)}%`;
      return {
        allowed: true,
        penalty: "partial",
        creditDelta: returned,
        partialBps,
        reason: "after_cutoff",
        explanation:
          fullReturn === 0
            ? `${cutoffCopy} Your recurring slot next week is unaffected and nothing has been charged.`
            : returned > 0
              ? `${cutoffCopy} This business gives back ${percentCopy} of a late session, which rounds up to 1 session back in your balance.`
              : `${cutoffCopy} This business gives back ${percentCopy} of a late session, which is less than a whole session, so this one is used up. Ask the business if you think it should go back.`,
      };
    }

    case "lose_credit":
    default:
      return {
        allowed: true,
        penalty: "lose_credit",
        creditDelta: 0,
        reason: "after_cutoff",
        explanation:
          fullReturn > 0
            ? `${cutoffCopy} This session will be used up if you confirm.`
            : `${cutoffCopy} Your recurring slot next week is unaffected and nothing has been charged.`,
      };
  }
}

/**
 * The booking-window half of the policy: `min_book_ahead_min` and `max_book_ahead_days`. Separate
 * from `evaluate` because booking has no credit consequence to preview, only a yes or a no.
 */
export function evaluateBooking(input: BookingPolicyInput): BookingPolicyOutcome {
  const { policy, sessionStartsAt, now, sessionCancelled } = input;

  if (sessionCancelled) {
    return {
      allowed: false,
      reason: "session_cancelled",
      explanation: "This session has been cancelled by the business.",
    };
  }

  const minutesLeft = (sessionStartsAt.getTime() - now.getTime()) / 60_000;

  if (minutesLeft <= 0) {
    return {
      allowed: false,
      reason: "session_started",
      explanation: "This session has already started.",
    };
  }

  if (minutesLeft < policy.minBookAheadMin) {
    return {
      allowed: false,
      reason: "too_soon",
      explanation:
        policy.minBookAheadMin % 60 === 0
          ? `This business closes bookings ${policy.minBookAheadMin / 60} ${
              policy.minBookAheadMin / 60 === 1 ? "hour" : "hours"
            } before a session starts.`
          : `This business closes bookings ${policy.minBookAheadMin} minutes before a session starts.`,
    };
  }

  // The group's own rules win over the business rules: the caller passes the policy of the
  // session's group, which is the group's own row when it has one (migration 0073).
  const daysAhead = minutesLeft / (60 * 24);
  if (daysAhead > policy.maxBookAheadDays) {
    return {
      allowed: false,
      reason: "too_far_ahead",
      explanation: `You can book this session up to ${policy.maxBookAheadDays} ${
        policy.maxBookAheadDays === 1 ? "day" : "days"
      } ahead. Come back closer to the date.`,
      opensAt: new Date(sessionStartsAt.getTime() - policy.maxBookAheadDays * 86_400_000),
    };
  }

  return { allowed: true, explanation: "This session is open for booking." };
}
